/**
 * Batch web push + behavioral events, ported from the legacy theme (BRD-416).
 *
 * Push (service worker registration + opt-in prompt) must run in the storefront
 * document, so it lives here rather than in a Shopify custom pixel: the pixel
 * sandbox is an opaque-origin iframe with no usable service worker. The Batch web
 * SDK keys are browser-public.
 *
 * Safety: this only loads the Batch SDK and attaches document listeners
 * (cart:update, subscribeToSale). It never mutates the cart, intercepts fetch, or
 * wraps Horizon handlers, so it cannot affect the commerce flow. Cart adds and
 * removes are derived by diffing a cart snapshot, the same approach as
 * klaviyo-cart-events.js, because Horizon's add event ships an empty cart payload.
 *
 * Gated on marketing consent via window.tbConsent (BRD-422). Purchase tracking is
 * intentionally omitted (off-theme checkout); account-creation events are dropped.
 */
(function () {
  if (window.__batchIntegration) return;
  window.__batchIntegration = true;

  var market = window.BATCH_MARKET || 'fr';
  var config = (window.BATCH_CONFIG && (window.BATCH_CONFIG[market] || window.BATCH_CONFIG.fr)) || null;
  if (!config) return;
  var swPath = window.BATCH_SW_PATH;

  function track(eventName, attributes) {
    try {
      window.batchSDK(function (api) {
        api.trackEvent(eventName, attributes ? { attributes: attributes } : undefined);
      });
    } catch (error) {
      /* tracking must never break the page */
    }
  }

  // ---- cart adds / removes via snapshot diff (Horizon `cart:update`) ----
  function snapshot(items) {
    var map = new Map();
    (items || []).forEach(function (item) {
      map.set(item.key || String(item.id), item);
    });
    return map;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  // Seed synchronously from the cart rendered into the page so the first change has a baseline.
  var previousItems = snapshot(window.BATCH_INITIAL_CART && window.BATCH_INITIAL_CART.items);

  function reconcile(items) {
    var next = snapshot(items);

    // Increases -> add_to_cart.
    next.forEach(function (current, key) {
      var prev = previousItems.get(key);
      var prevQty = prev ? prev.quantity || 0 : 0;
      var nextQty = current.quantity || 0;
      if (nextQty > prevQty) {
        track('add_to_cart', {
          item_name: current.product_title || current.title || '',
          item_brand: current.vendor || '',
          item_category: current.product_type || '',
          item_id: current.id,
          item_price: (current.price || 0) / 100,
          event_emmiter: 'horizon-cart-update'
        });
      }
    });

    // Decreases -> remove_from_cart.
    previousItems.forEach(function (prev, key) {
      var current = next.get(key);
      var prevQty = prev.quantity || 0;
      var nextQty = current ? current.quantity || 0 : 0;
      if (nextQty < prevQty) {
        track('remove_from_cart', {
          item_name: prev.product_title || prev.title || '',
          item_brand: prev.vendor || '',
          item_id: prev.id
        });
      }
    });

    previousItems = next;
  }

  var cartEventSeq = 0;
  function onCartUpdate(event) {
    var seq = ++cartEventSeq;
    var resource = event && event.detail && event.detail.resource;
    if (resource && Array.isArray(resource.items)) {
      // Full cart payload (cart page / drawer change): diff synchronously.
      reconcile(resource.items);
    } else {
      // Add event ships an empty resource. Fetch the cart to diff; drop a stale
      // fetch if a newer cart event arrived meanwhile.
      fetchCart()
        .then(function (cart) {
          if (seq === cartEventSeq) reconcile(cart.items);
        })
        .catch(function () {});
    }
  }

  function onSubscribeToSale(event) {
    var detail = (event && event.detail) || {};
    var attributes = { productVendor: detail.brand_name || '' };
    if (detail.start_date) attributes.start_date = new Date(detail.start_date).toISOString();
    if (detail.end_date) attributes.end_date = new Date(detail.end_date).toISOString();
    track('subscribed_upcoming_sale', attributes);
  }

  // ---- Batch SDK bootstrap + push setup ----
  function loadSdk() {
    (function (b, a, t, c) {
      var key = 'batchSDK';
      b[key] = b[key] || function () {
        (b[key].q = b[key].q || []).push(arguments);
      };
      var element = a.createElement(t);
      var first = a.getElementsByTagName(t)[0];
      element.async = 1;
      element.src = c;
      first.parentNode.insertBefore(element, first);
    })(window, document, 'script', 'https://via.batch.com/v4/bootstrap.min.js');
  }

  function uiConfig() {
    var ua = navigator.userAgent;
    var isFirefox = ua.indexOf('Firefox') !== -1;
    var isSafari = ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1;
    // Firefox and Safari get the custom branded prompt; everyone else the native one.
    if (isFirefox || isSafari) {
      return {
        alert: {
          text: config.text,
          positiveSubBtnLabel: config.positiveSubBtnLabel,
          negativeBtnLabel: config.negativeBtnLabel,
          positiveBtnStyle: {
            backgroundColor: '#002961',
            hoverBackgroundColor: '#00439E',
            textColor: 'white'
          }
        }
      };
    }
    return { native: {} };
  }

  // Re-register the push subscription if the VAPID key changed (ported from legacy).
  function reregisterPushIfNeeded(serverKey) {
    var unsafe = serverKey.replace(/-/g, '+').replace(/_/g, '/');
    var reencoded = window.btoa(window.atob(unsafe));
    navigator.serviceWorker
      .getRegistration()
      .then(function (sw) {
        return sw && sw.pushManager.getSubscription();
      })
      .then(function (sub) {
        return sub && sub.options.applicationServerKey;
      })
      .then(function (key) {
        return key ? btoa(String.fromCharCode.apply(String, new Uint8Array(key))) : null;
      })
      .then(function (key) {
        if (key && key !== reencoded) {
          navigator.serviceWorker
            .getRegistration()
            .then(function (reg) {
              return reg.unregister();
            })
            .then(function () {
              window.batchSDK(function (api) {
                api.subscribe();
              });
            })
            .catch(function () {});
        }
      })
      .catch(function () {});
  }

  function firePageEvents() {
    var page = window.BATCH_PAGE || {};
    if (page.screen_name) track('screen_view', { screen_name: page.screen_name });
    if (page.view_item) track('view_item', page.view_item);
    if (page.view_item_list) track('view_item_list', page.view_item_list);
    if (page.ongoing_item_press) {
      var op = page.ongoing_item_press;
      var attributes = {
        name: op.name,
        id: op.id,
        categories: op.categories,
        isDisplayedInAllSales: op.isDisplayedInAllSales
      };
      if (op.start_date) attributes.start_date = new Date(op.start_date).toISOString();
      if (op.end_date) attributes.end_date = new Date(op.end_date).toISOString();
      track('ongoing_item_press', attributes);
    }
  }

  function start() {
    var swSupported = 'serviceWorker' in navigator && window.isSecureContext;
    loadSdk();

    var setupOptions = {
      apiKey: config.apiKey,
      subdomain: config.subdomain,
      authKey: config.authKey,
      vapidPublicKey: config.vapidPublicKey,
      ui: uiConfig()
    };
    // Push needs a same-origin service worker (served via the Batch App Proxy).
    if (swSupported) {
      setupOptions.serviceWorker = {
        automaticallyRegister: false,
        registration: navigator.serviceWorker.register(swPath)
      };
    }
    window.batchSDK('setup', setupOptions);

    // Language / region.
    window.batchSDK(function (api) {
      api.profile().then(function (profile) {
        profile.edit(function (editor) {
          editor.setLanguage(market).setRegion(market.toUpperCase());
        });
      });
    });

    // Identify the logged-in customer, clear identity otherwise.
    var customer = window.BATCH_CUSTOMER;
    window.batchSDK(function (api) {
      api.profile().then(function (profile) {
        if (customer && customer.id) {
          profile.identify({ customId: String(customer.id) });
          profile.edit(function (editor) {
            if (customer.firstName) editor.setAttribute('firstname', customer.firstName);
            if (customer.gender) editor.setAttribute('gender', customer.gender);
          });
        } else {
          profile.identify(null);
        }
      });
    });

    if (swSupported) reregisterPushIfNeeded(config.vapidPublicKey);

    firePageEvents();
    document.addEventListener('cart:update', onCartUpdate);
    document.addEventListener('subscribeToSale', onSubscribeToSale);
  }

  if (window.tbConsent && typeof window.tbConsent.whenAllowed === 'function') {
    window.tbConsent.whenAllowed('marketing', start);
  } else {
    start();
  }
})();
